import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button, Input, Spinner } from './ui/components';
import { Icon } from './ui/icons';
import { AuthShowcase, Eyebrow } from './pages/auth-redesign/showcase';

const API = process.env.REACT_APP_API_URL;
const POST_SIGNIN = '/lock';

// Landing page for the emailed magic link: /sign-in/:token
//
// Two steps on purpose. Opening the page only *peeks* at the link — it reports
// whether a second factor is still owed and leaves the link unspent. Nothing is
// consumed until the person is here and has answered any challenge. Mail scanners
// and link previewers fetch what lands in an inbox, so peek is a POST (they issue
// GETs) and consume happens only on a real interaction.
//
// The earlier version posted straight to consume with just the token, which works
// for an un-enrolled account but is refused for anyone with an authenticator — the
// API demands a code it was never asked for, so every 2FA user's link dead-ended.
const SignInLink = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [state, setState] = useState<'checking' | 'signing' | 'needs-code' | 'dead'>('checking');
  const [emailMasked, setEmailMasked] = useState('');
  const [totp, setTotp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const peekedRef = useRef(false);

  const finish = useCallback(async (payload: { totp?: string; backupCode?: string }) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/Authentication/magic-link/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, ...payload }),
      });
      if (!res.ok) {
        // 400 here is a wrong/needed code, not a dead link — keep the person on the
        // challenge so they can retry rather than bouncing them to "expired".
        const body = await res.text().catch(() => '');
        setError(body && body.length < 120 ? body.replace(/^"|"$/g, '') : 'That code is not correct. Try again.');
        setBusy(false);
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      login();
      navigate(data && data.setupRequired && data.setupRequired !== 'none' ? '/setup' : POST_SIGNIN, { replace: true });
    } catch (e) {
      setError('An unexpected error occurred. Please try again.');
      setBusy(false);
    }
  }, [token, login, navigate]);

  useEffect(() => {
    if (peekedRef.current) return; // one token, one peek (StrictMode double-mount guard)
    peekedRef.current = true;

    (async () => {
      if (!token) { setState('dead'); return; }
      try {
        const res = await fetch(`${API}/Authentication/magic-link/peek`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (!res.ok) { setState('dead'); return; }
        const info = await res.json().catch(() => ({} as any));
        setEmailMasked(info?.emailMasked || '');
        if (info?.twoFactorEnabled) {
          setState('needs-code');   // hand off to the code form; consume waits for it
        } else {
          setState('signing');
          finish({});               // nothing more to ask — spend it and go
        }
      } catch (e) {
        setState('dead');
      }
    })();
  }, [token, finish]);

  if (state === 'checking' || state === 'signing') {
    return (
      <AuthShowcase>
        <div className="authx-form">
          <header className="authx-form__head">
            <Eyebrow>Passwordless sign-in</Eyebrow>
            <h1 className="authx-form__title">Signing you in</h1>
            <p className="authx-form__sub">Hold on a moment while we finish signing you in to TableView.</p>
          </header>
          <div className="authx-form__actions">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--color-foreground-muted)' }}>
              <Spinner size={20} /> One moment…
            </span>
          </div>
        </div>
      </AuthShowcase>
    );
  }

  if (state === 'dead') {
    return (
      <AuthShowcase>
        <div className="authx-form">
          <header className="authx-form__head">
            <Eyebrow>Sign-in link</Eyebrow>
            <h1 className="authx-form__title">That link has expired</h1>
            <p className="authx-form__sub">Sign-in links last 20 minutes and work once. Ask for a new one.</p>
          </header>
          <div className="authx-form__actions">
            <Button
              type="button"
              variant="primary"
              fullWidth
              size="lg"
              leadingIcon={<Icon.ArrowRight size={16} />}
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </AuthShowcase>
    );
  }

  // Second factor — authenticator code or backup code.
  return (
    <AuthShowcase>
      <form
        className="authx-form"
        onSubmit={(e) => { e.preventDefault(); finish(useBackup ? { backupCode } : { totp }); }}
      >
        <header className="authx-form__head">
          <Eyebrow>Two-step verification</Eyebrow>
          <h1 className="authx-form__title">One more step</h1>
          <p className="authx-form__sub">
            Signing in as <strong>{emailMasked}</strong>.{' '}
            {useBackup
              ? 'Enter one of your backup codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </header>

        {error && (
          <div className="authx-alert authx-alert--error" role="alert">
            <Icon.CircleAlert size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="authx-form__fields">
          {useBackup ? (
            <Input
              label="Backup code"
              type="text"
              value={backupCode}
              placeholder="XXXX-XXXX"
              autoComplete="one-time-code"
              leadingIcon={<Icon.Lock size={16} />}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBackupCode(e.target.value)}
            />
          ) : (
            <Input
              label="Authenticator code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totp}
              placeholder="000000"
              autoComplete="one-time-code"
              autoFocus
              leadingIcon={<Icon.Lock size={16} />}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotp(e.target.value.replace(/\D/g, ''))}
            />
          )}
        </div>

        <div className="authx-form__actions">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            loading={busy}
            disabled={useBackup ? !backupCode.trim() : totp.length < 6}
          >
            Continue
          </Button>
          <Button
            type="button"
            variant="link"
            fullWidth
            onClick={() => { setUseBackup(!useBackup); setError(''); setTotp(''); setBackupCode(''); }}
          >
            {useBackup ? 'Use my authenticator app instead' : 'I cannot reach my authenticator'}
          </Button>
        </div>
      </form>
    </AuthShowcase>
  );
};

export default SignInLink;
